const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class XtreamApiService {
  constructor() {
    this.serverUrl = null;
    this.username = null;
    this.password = null;
    this.serverId = 0;
  }

  setCredentials(serverUrl, username, password, serverId = 0) {
    this.serverUrl = serverUrl;
    this.username = username;
    this.password = password;
    this.serverId = serverId;
  }

  clearCredentials() {
    this.serverUrl = null;
    this.username = null;
    this.password = null;
    this.serverId = 0;
  }

  isAuthenticated() {
    return !!(this.serverUrl && this.username && this.password);
  }

  buildParams() {
    return new URLSearchParams({
      username: this.username,
      password: this.password,
      server_id: String(this.serverId)
    });
  }

  async getServers() {
    const res = await fetch(`${BACKEND_URL}/api/xtream/servers`);
    return res.json();
  }

  async login(username, password, serverId = 0) {
    const response = await fetch(`${BACKEND_URL}/api/xtream/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, server_id: serverId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    this.setCredentials(data.server_url, username, password, serverId);
    return data;
  }

  async getLiveCategories() {
    const params = this.buildParams();
    const response = await fetch(`${BACKEND_URL}/api/xtream/live-categories?${params}`);
    if (!response.ok) throw new Error('Failed to fetch live categories');
    return response.json();
  }

  async getLiveStreams(categoryId = null) {
    const params = this.buildParams();
    if (categoryId) params.append('category_id', categoryId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/live-streams?${params}`);
    if (!response.ok) throw new Error('Failed to fetch live streams');
    return response.json();
  }

  async getVodCategories() {
    const params = this.buildParams();
    const response = await fetch(`${BACKEND_URL}/api/xtream/vod-categories?${params}`);
    if (!response.ok) throw new Error('Failed to fetch VOD categories');
    return response.json();
  }

  async getVodStreams(categoryId = null) {
    const params = this.buildParams();
    if (categoryId) params.append('category_id', categoryId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/vod-streams?${params}`);
    if (!response.ok) throw new Error('Failed to fetch VOD streams');
    return response.json();
  }

  async getVodInfo(vodId) {
    const params = this.buildParams();
    params.append('vod_id', vodId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/vod-info?${params}`);
    if (!response.ok) throw new Error('Failed to fetch VOD info');
    return response.json();
  }

  async getSeriesCategories() {
    const params = this.buildParams();
    const response = await fetch(`${BACKEND_URL}/api/xtream/series-categories?${params}`);
    if (!response.ok) throw new Error('Failed to fetch series categories');
    return response.json();
  }

  async getSeries(categoryId = null) {
    const params = this.buildParams();
    if (categoryId) params.append('category_id', categoryId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/series?${params}`);
    if (!response.ok) throw new Error('Failed to fetch series');
    return response.json();
  }

  async getSeriesInfo(seriesId) {
    const params = this.buildParams();
    params.append('series_id', seriesId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/series-info?${params}`);
    if (!response.ok) throw new Error('Failed to fetch series info');
    return response.json();
  }

  async getEpg(streamId) {
    const params = this.buildParams();
    params.append('stream_id', streamId);
    const response = await fetch(`${BACKEND_URL}/api/xtream/epg?${params}`);
    if (!response.ok) return null;
    return response.json();
  }

  async getShortEpg(streamId, limit = 4) {
    const params = this.buildParams();
    params.append('stream_id', streamId);
    params.append('limit', limit);
    const response = await fetch(`${BACKEND_URL}/api/xtream/short-epg?${params}`);
    if (!response.ok) return null;
    return response.json();
  }

  getStreamUrl(streamId, type = 'live', container = 'ts') {
    if (!this.serverUrl || !this.username || !this.password) {
      throw new Error('Not authenticated');
    }

    const baseUrl = this.serverUrl.replace(/\/$/, '');

    switch (type) {
      case 'live':
        return `${baseUrl}/live/${this.username}/${this.password}/${streamId}.${container}`;
      case 'movie':
        return `${baseUrl}/movie/${this.username}/${this.password}/${streamId}.${container}`;
      case 'series':
        return `${baseUrl}/series/${this.username}/${this.password}/${streamId}.${container}`;
      default:
        throw new Error('Invalid stream type');
    }
  }

  // Favorites
  async addFavorite(contentId, contentType, name, poster, rating) {
    await fetch(`${BACKEND_URL}/api/favorites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: this.username, content_id: String(contentId), content_type: contentType, name, poster, rating })
    });
  }

  async removeFavorite(contentId, contentType) {
    await fetch(`${BACKEND_URL}/api/favorites?username=${this.username}&content_id=${contentId}&content_type=${contentType}`, { method: 'DELETE' });
  }

  async getFavorites(contentType = null) {
    const params = new URLSearchParams({ username: this.username });
    if (contentType) params.append('content_type', contentType);
    const res = await fetch(`${BACKEND_URL}/api/favorites?${params}`);
    return res.json();
  }

  async checkFavorite(contentId, contentType) {
    const res = await fetch(`${BACKEND_URL}/api/favorites/check?username=${this.username}&content_id=${contentId}&content_type=${contentType}`);
    const data = await res.json();
    return data.is_favorite;
  }

  // Watch Progress
  async saveWatchProgress(contentId, contentType, name, poster, currentTime, duration, season, episode, episodeId) {
    await fetch(`${BACKEND_URL}/api/watch-progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: this.username, content_id: String(contentId), content_type: contentType,
        name, poster, current_time: currentTime, duration,
        season: season || null, episode: episode || null, episode_id: episodeId ? String(episodeId) : null
      })
    });
  }

  async getWatchProgress() {
    const res = await fetch(`${BACKEND_URL}/api/watch-progress?username=${this.username}`);
    return res.json();
  }

  async getItemProgress(contentId, contentType, episodeId = null) {
    const params = new URLSearchParams({ username: this.username, content_id: String(contentId), content_type: contentType });
    if (episodeId) params.append('episode_id', String(episodeId));
    const res = await fetch(`${BACKEND_URL}/api/watch-progress/item?${params}`);
    return res.json();
  }

  async getTmdbCast(tmdbId) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/cast/${tmdbId}`);
    const data = await res.json();
    return data.cast || [];
  }

  async getActorMovies(personId) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/person/${personId}/movies`);
    const data = await res.json();
    return data.movies || [];
  }

  async getSimilarMovies(tmdbId) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/movie/${tmdbId}/similar`);
    const data = await res.json();
    return data.movies || [];
  }

  async getTvCast(tmdbId) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/tv/${tmdbId}/cast`);
    const data = await res.json();
    return data.cast || [];
  }

  async getSimilarSeries(tmdbId) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/tv/${tmdbId}/similar`);
    const data = await res.json();
    return data.series || [];
  }

  async lookupTvByName(name) {
    const res = await fetch(`${BACKEND_URL}/api/tmdb/tv/lookup?name=${encodeURIComponent(name)}`);
    return res.json();
  }

  async search(query) {
    const params = this.buildParams();
    params.append('q', query);
    const res = await fetch(`${BACKEND_URL}/api/xtream/search?${params}`);
    return res.json();
  }
}

export const xtreamApi = new XtreamApiService();
export default xtreamApi;
